import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, Mail, Phone, MapPin, Star, Award, Users, Heart } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VoteButton } from "@/components/voting/VoteButton";

interface SportProfile {
  id: string;
  category: string;
  country: string;
  ranking: number;
  full_name: string;
  slug: string;
  bio: string | null;
  profile_image: string | null;
  email: string;
  phone: string | null;
  location_city: string | null;
  location_country: string;
  specialty: string[] | null;
  certifications: string[] | null;
  achievements: string[] | null;
  years_experience: number | null;
  clients_trained: number | null;
  hourly_rate: number | null;
  sport_background: string | null;
}

const countries = [
  "España", "México", "Argentina", "Colombia", "Venezuela", "Perú", 
  "Chile", "Ecuador", "Guatemala", "Cuba", "Bolivia", "República Dominicana",
  "Honduras", "Paraguay", "El Salvador", "Nicaragua", "Costa Rica", 
  "Puerto Rico", "Uruguay", "Estados Unidos"
];

const SportCategoryProfessionals = () => {
  const { category } = useParams<{ category: string }>();
  const [profiles, setProfiles] = useState<SportProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("España");

  const categoryName = category
    ?.split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "";

  useEffect(() => {
    fetchProfiles();
  }, [category, selectedCountry]);

  const fetchProfiles = async () => {
    if (!category) return;

    setLoading(true);
    
    // Country code mapping
    const countryCodeMap: Record<string, string> = {
      "España": "ES",
      "México": "MX",
      "Argentina": "AR",
      "Colombia": "CO",
      "Venezuela": "VE",
      "República Dominicana": "DO",
      "Brasil": "BR",
      "Chile": "CL",
      "Perú": "PE",
      "Uruguay": "UY",
      "Ecuador": "EC",
      "Estados Unidos": "US",
      "Guatemala": "GT",
      "Bolivia": "BO",
      "Honduras": "HN",
      "Paraguay": "PY",
      "El Salvador": "SV",
      "Nicaragua": "NI",
      "Costa Rica": "CR",
      "Puerto Rico": "PR",
      "Cuba": "CU",
    };
    
    const countryCode = countryCodeMap[selectedCountry] ?? selectedCountry;
    
    // Fetch profiles with flexible matching for category (case-insensitive) and country (both code and full name)
    const { data, error } = await supabase
      .from("sport_category_profiles")
      .select("*")
      .eq("is_active", true)
      .order("ranking", { ascending: true });

    if (!error && data) {
      // Filter in JavaScript to handle case-insensitive category matching and flexible country matching
      // Slug-normalized category match: DB values are canonical slugs
      // ("futbol"), while old links may still carry accents ("fútbol").
      const slugify = (value?: string | null) =>
        (value || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");

      const filtered = data.filter(profile => {
        const categoryMatch = slugify(profile.category) === slugify(category);

        
        // Country match - support both codes (ES, MX) and full names (España, México)
        const countryMatch = 
          profile.country === countryCode || 
          profile.country === selectedCountry ||
          profile.location_country === countryCode ||
          profile.location_country === selectedCountry;
        
        return categoryMatch && countryMatch;
      });
      
      // Deduplicar por slug para evitar perfiles repetidos en la UI
      const profileMap = new Map<string, SportProfile>();
      for (const p of filtered) {
        if (!profileMap.has(p.slug)) profileMap.set(p.slug, p);
      }
      setProfiles(Array.from(profileMap.values()));
    } else if (error) {
      console.error("Error fetching profiles:", error);
    }
    
    setLoading(false);
  };
  const getMedalColor = (ranking: number) => {
    switch (ranking) {
      case 1: return "text-yellow-500";
      case 2: return "text-gray-400";
      case 3: return "text-amber-600";
      default: return "text-primary";
    }
  };

  return (
    <>
      <SEO 
        title={`Los Mejores en ${categoryName} - Top 3 por País`}
        description={`Descubre los 3 mejores profesionales de ${categoryName} en cada país de habla hispana. Perfiles completos con certificaciones, experiencia y contacto.`}
      />
      <Navigation />
      
      <main className="pt-32 lg:pt-40 pb-16 min-h-screen">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Trophy className="w-12 h-12 text-primary" />
              <h1 className="text-4xl md:text-5xl font-bold">
                Los Mejores en {categoryName}
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Top 3 profesionales por país de habla hispana
            </p>
            <div className="flex gap-4 justify-center mt-6">
              <Button size="lg" variant="default" asChild>
                <a href="#voting-section">
                  <Heart className="w-5 h-5 mr-2" />
                  ¡Votar!
                </a>
              </Button>
              <Button size="lg" variant="secondary" asChild>
                <Link to="/sport-professional-register">
                  Participar
                </Link>
              </Button>
            </div>
          </div>

          {/* Country Tabs */}
          <Tabs value={selectedCountry} onValueChange={setSelectedCountry} className="mb-8">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 h-auto p-2">
              {countries.map((country) => (
                <TabsTrigger key={country} value={country} className="text-sm">
                  {country}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Profiles Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Cargando perfiles...</p>
            </div>
          ) : profiles.length === 0 ? (
            <div className="text-center py-16 space-y-6 max-w-2xl mx-auto">
              <div className="bg-secondary/20 rounded-full w-24 h-24 mx-auto flex items-center justify-center mb-4">
                <Trophy className="w-12 h-12 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">¡Sé el Primero!</h3>
              <p className="text-muted-foreground text-lg">
                Aún no hay profesionales registrados en {categoryName} para {selectedCountry}.
              </p>
              <div className="bg-primary/5 rounded-lg p-6 space-y-4">
                <p className="font-semibold">¿Por qué registrarte ahora?</p>
                <ul className="text-left space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Trophy className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Serás el #1 automáticamente al ser el primero</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Mayor visibilidad al ser pionero en tu categoría</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Construye tu reputación desde el inicio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Award className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Pago único de €29 - Acceso de por vida</span>
                  </li>
                </ul>
              </div>
              <Button size="lg" asChild className="w-full max-w-md">
                <Link to="/sport-professional-register">
                  <Trophy className="w-5 h-5 mr-2" />
                  Registrarte como Profesional
                </Link>
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {profiles.map((profile) => (
                <Link key={profile.id} to={`/los-mejores/${category}/${profile.slug}`}>
                  <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-105">
                  <CardContent className="p-6">
                    {/* Ranking Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="outline" className="gap-1">
                        <Trophy className={`w-4 h-4 ${getMedalColor(profile.ranking)}`} />
                        #{profile.ranking}
                      </Badge>
                      <Badge>{profile.location_country}</Badge>
                    </div>

                    {/* Profile Image */}
                    <div className="aspect-square rounded-lg bg-secondary/20 mb-4 overflow-hidden">
                      {profile.profile_image ? (
                        <img 
                          src={profile.profile_image} 
                          alt={profile.full_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.src = "/default-listing-fallback.png";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-16 h-16 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Name */}
                    <h3 className="text-xl font-bold mb-2">{profile.full_name}</h3>

                    {/* Location */}
                    {profile.location_city && (
                      <div className="flex items-center gap-2 text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{profile.location_city}</span>
                      </div>
                    )}

                    {/* Bio */}
                    {profile.bio && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                        {profile.bio}
                      </p>
                    )}

                    {/* Stats (sin años de experiencia) */}
                    <div className="grid grid-cols-1 gap-4 mb-4">
                      {profile.clients_trained && (
                        <div className="text-center p-2 bg-secondary/20 rounded">
                          <div className="font-bold text-lg">{profile.clients_trained}+</div>
                          <div className="text-xs text-muted-foreground">Clientes</div>
                        </div>
                      )}
                    </div>

                    {/* Certifications */}
                    {profile.certifications && profile.certifications.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Award className="w-4 h-4 text-primary" />
                          <span className="text-sm font-semibold">Certificaciones</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {profile.certifications.slice(0, 2).map((cert, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {cert}
                            </Badge>
                          ))}
                          {profile.certifications.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{profile.certifications.length - 2}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}


                    {/* Voting Section */}
                    <div id="voting-section">
                      <VoteButton
                        listingId={profile.id}
                        citySlug={profile.location_country.toLowerCase()}
                        activitySlug={category || ''}
                        currentVotes={0}
                      />
                    </div>
                  </CardContent>
                </Card>
                </Link>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 text-center p-8 bg-gradient-primary rounded-lg">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              ¿Eres un profesional destacado en {categoryName}?
            </h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Únete a nuestra plataforma y compite por estar entre los 3 mejores de tu país
            </p>
            <Button size="lg" className="bg-gradient-primary hover:opacity-90 transition-opacity" asChild>
              <Link to="/sport-professional-register">
                Registrarte como Profesional
              </Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default SportCategoryProfessionals;
